import { type FC, type RefObject, useCallback, useState } from 'react';

import type { Editor } from '@tiptap/react';
import cn from 'classnames';

import type { VideoUploadLimits } from '@/types';

import type {
  CommunityPostComposerProps,
  ComposerAttachableLists,
  ComposerScheduling,
} from '../../CommunityPostComposer.type';
import { tr } from '../constants';
import { EmojiPanel } from '../panels/EmojiPanel';
import { LinkPopover } from '../panels/LinkPopover';
import { MediaPanel } from '../panels/MediaPanel';
import { PostsPanel } from '../panels/PostsPanel';
import { VideoPanel } from '../panels/VideoPanel';
import { DisabledToolbar } from '../toolbar/DisabledToolbar';
import { FormatSubBar } from '../toolbar/FormatSubBar';
import { TextTIcon } from '../toolbar/icons';
import { ToolbarButton } from '../toolbar/ToolbarButton';
import { ComposerFrameFooter } from './ComposerFrame';
import { ScheduleClockButton } from './ComposerSchedule';
import { openScheduling } from './openScheduling';

interface ComposerFooterProps {
  editor: Editor | null;
  isEditMode: boolean;
  isSubmitting: boolean;
  isSubmitDisabled: boolean;
  onSubmit?: () => void;
  scheduling?: ComposerScheduling;
  clockRef?: RefObject<HTMLButtonElement | null>;
  translations?: Record<string, string>;
  onUploadImage?: CommunityPostComposerProps['onUploadImage'];
  isMediaUploadDisabled?: CommunityPostComposerProps['isMediaUploadDisabled'];
  uploadAccept?: string;
  uploadMaxBytes?: number;
  // Present only when the host enabled video uploads. onSelectVideoFile hands a
  // picked/dropped file to the composer; the placeholder, progress, and errors live
  // in the editor, so the panel is just file selection.
  onSelectVideoFile?: (file: File) => void;
  videoUploadLimits: VideoUploadLimits;
  videoUploadAccept?: string;
  locale?: string;
  videoPanelOpen?: boolean;
  onVideoPanelOpenChange?: (open: boolean) => void;
  onSearchGifs?: CommunityPostComposerProps['onSearchGifs'];
  gifSearchLimit?: number;
  gifSearchDebounceMs?: number;
  attachableLists?: ComposerAttachableLists;
  onPostQueryChange?: (query: string) => void;
  isLoadingPosts?: boolean;
  attached?: ComposerAttachableLists;
  onAttachedChange?: (next: ComposerAttachableLists) => void;
  maxAttachedPosts: number;
  transformImageUrl?: (url: string, width: number) => string;
}

const submitLabelKeys = (isEditMode: boolean): [string, string] =>
  isEditMode
    ? ['community.composer.save', 'community.composer.saving']
    : ['community.composer.submit', 'community.composer.submitting'];

export const ComposerFooter: FC<ComposerFooterProps> = ({
  editor,
  isEditMode,
  isSubmitting,
  isSubmitDisabled,
  onSubmit,
  scheduling,
  clockRef,
  translations,
  onUploadImage,
  isMediaUploadDisabled,
  uploadAccept,
  uploadMaxBytes,
  onSelectVideoFile,
  videoUploadLimits,
  videoUploadAccept,
  locale,
  videoPanelOpen,
  onVideoPanelOpenChange,
  onSearchGifs,
  gifSearchLimit,
  gifSearchDebounceMs,
  attachableLists,
  onPostQueryChange,
  isLoadingPosts,
  attached,
  onAttachedChange,
  maxAttachedPosts,
  transformImageUrl,
}) => {
  const [formatBarOpen, setFormatBarOpen] = useState(false);

  const toggleFormatBar = useCallback(() => setFormatBarOpen((open) => !open), []);
  const showMedia = Boolean(onUploadImage || onSearchGifs);
  const showPosts = Boolean(onPostQueryChange && onAttachedChange);
  const disabled = isSubmitting || isSubmitDisabled;

  const [submitKey, submittingKey] = submitLabelKeys(isEditMode);

  return (
    <ComposerFrameFooter
      topBar={editor && formatBarOpen && <FormatSubBar editor={editor} translations={translations} />}
    >
      {editor ? (
        <div className="flex gap-2 items-center">
          <ToolbarButton
            label={tr(translations, 'community.composer.format_text')}
            icon={<TextTIcon />}
            onClick={toggleFormatBar}
            active={formatBarOpen}
          />
          <EmojiPanel editor={editor} translations={translations} />
          {showMedia && (
            <MediaPanel
              editor={editor}
              translations={translations}
              onUploadImage={onUploadImage}
              isMediaUploadDisabled={isMediaUploadDisabled}
              uploadAccept={uploadAccept}
              uploadMaxBytes={uploadMaxBytes}
              onSearchGifs={onSearchGifs}
              gifSearchLimit={gifSearchLimit}
              gifSearchDebounceMs={gifSearchDebounceMs}
            />
          )}
          {onSelectVideoFile && (
            <VideoPanel
              onSelectFile={onSelectVideoFile}
              limits={videoUploadLimits}
              locale={locale}
              translations={translations}
              accept={videoUploadAccept}
              open={videoPanelOpen}
              onOpenChange={onVideoPanelOpenChange}
            />
          )}
          <LinkPopover editor={editor} translations={translations} />
          {showPosts && onPostQueryChange && onAttachedChange && (
            <PostsPanel
              translations={translations}
              posts={attachableLists?.posts ?? []}
              onPostQueryChange={onPostQueryChange}
              isLoadingPosts={isLoadingPosts}
              attachedPosts={attached?.posts ?? []}
              onAttachedPostsChange={(nextPosts) => onAttachedChange({ ...attached, posts: nextPosts })}
              maxAttachedPosts={maxAttachedPosts}
              transformImageUrl={transformImageUrl}
            />
          )}
        </div>
      ) : (
        <DisabledToolbar translations={translations} showMedia={showMedia} />
      )}
      <div className="flex items-center gap-2">
        {scheduling && (
          <ScheduleClockButton
            buttonRef={clockRef}
            queuedCount={scheduling.queuedCount}
            onOpen={() => openScheduling(editor, scheduling.onOpen)}
            disabled={isSubmitting}
            translations={translations}
          />
        )}
        {onSubmit && (
          <button
            type="button"
            disabled={disabled}
            onClick={onSubmit}
            className={cn(
              'inline-flex h-10 items-center justify-center rounded-full px-5 text-[13px] font-medium leading-4 tracking-[-0.13px] bg-[var(--community-primary,#000)] text-[var(--community-text-on-primary,#fff)] transition-opacity',
              {
                'opacity-50 cursor-not-allowed': disabled,
                'hover:opacity-90': !disabled,
              },
            )}
          >
            {tr(translations, isSubmitting ? submittingKey : submitKey)}
          </button>
        )}
      </div>
    </ComposerFrameFooter>
  );
};
